"use client";

import React, { useRef, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import sanitizeAndParseInlineHTML from "@/utils/functions/sanitizeAndParseInlineHTML";
import { getCompletionTimeText } from "../../utils/getCompletionTimeText";
import { ctaBackgroundColors } from "../../utils/ctaBackgroundColors";
import { getTypeBackgroundColor } from "../../utils/mapTypeToColor";
import useWebview from "@/webview/useWebview";
import { NewProductCardProps } from "../../types";
import { renderBadges } from "../../utils/renderBadges";
import {
  getBrandStringFromPartnerKeys,
  getTagularSubject,
  setCorrelationId,
} from "@/utils/tagular/helpers";
import {
  FormatSubType,
  FormatType,
  NYI_NUMBER,
  NYI_STRING,
} from "@/constants/tagular/main";
import { ProductClicked } from "@/hooks/eventing/types/redventures.ecommerce.v1.ProductClicked";
import { translateVariant } from "@/utils/tagular/dataTranslationMatrices";
import { createCustomDimensions } from "@/utils/tagular/createDimension";
import useProductEvents from "@/hooks/eventing/useProductEvents";

interface HoverOverProps extends NewProductCardProps {
  hrefWithQueryParams?: string;
}
const getLineHeight = (titleLineCount: number, hasBadges: boolean) => {
  if (!hasBadges) {
    return titleLineCount === 1
      ? "leading-[1.24rem] [&_*]:leading-[1.24rem]"
      : "leading-[1.19rem] [&_*]:leading-[1.19rem]";
  }
  return titleLineCount === 1
    ? "leading-[1.19rem] [&_*]:leading-[1.19rem]"
    : "leading-[1.14rem] [&_*]:leading-[1.14rem]";
};

const getWebkitLineClamp = (
  titleLineCount: number,
  shouldShowCompletionTimeAndLevel: boolean,
) => {
  if (!shouldShowCompletionTimeAndLevel) {
    return titleLineCount === 1 ? 12 : 11;
  }
  return titleLineCount === 1 ? 11 : 10;
};

export function HoverOver(props: HoverOverProps) {
  const { getUrl } = useWebview();
  const titleRef = useRef<HTMLDivElement>(null);
  const [titleLineCount, setTitleLineCount] = useState(2); // Default to 2 lines

  const ctaText = props.hoverEffectCta?.text || "Learn more";
  const completionTimeText = getCompletionTimeText(
    props.weeksToComplete,
    false,
  );
  const index = props.index || -1;
  const tagularProduct: ProductClicked["product"] = {
    brand: getBrandStringFromPartnerKeys(
      Array.isArray(props.partnerFullName)
        ? props.partnerFullName
        : [props.partnerFullName ?? ""],
    ),
    formatSubtype: FormatSubType.Grid,
    formatType: FormatType.ProductCardCta,
    location: props.location || NYI_STRING,
    name: props.name,
    variant: props.tagular_variant || translateVariant(props.type),
    position: index < 0 ? NYI_NUMBER : index + 1,
    category: getTagularSubject(props.subjects),
    productId: props.uuid,
    text: ctaText,
  };

  const customDimensions: ProductClicked["customDimensions"] =
    createCustomDimensions([
      ["level", props.level],
      ["pacing", props.pacing],
      ["isPartOfProgram", props.isPartOfProgram],
      ["learningType", props.type],
      ["courseCount", props.childCoursesCount],
      ["weeksToComplete", props.weeksToComplete],
      ["searchQueryId", props.searchQueryId],
    ]);

  const { productClicked, correlationId } = useProductEvents({
    product: tagularProduct,
    customDimensions,
  });

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    props.onClick?.(e);
  };

  const handleClickCta = () => {
    setCorrelationId(correlationId);
    productClicked({ listId: props.listId });
  };

  const badges = renderBadges({
    monarchBadges: props.badges,
    type: props.type,
    badgesByProduct: props.badgesByProduct,
    popularItemList: props.popularItemList,
    uuid: props.uuid,
    programType: props.programType,
    microDegreeTag: props.microDegreeTag,
    popularBadgeOverride: props.popularBadgeOverride,
  });

  // Calculate typeBackgroundColor
  const colorKey = props.cta?.backgroundColor || "";
  const typeBackgroundColor =
    ctaBackgroundColors[colorKey] ?? getTypeBackgroundColor(props.type);

  const hideCta = props.hoverEffectCta?.hideCta || false;
  // Detect title line count
  useEffect(() => {
    if (titleRef.current) {
      const lineHeight = parseInt(
        getComputedStyle(titleRef.current).lineHeight,
      );
      const height = titleRef.current.offsetHeight;
      const lines = Math.round(height / lineHeight);
      setTitleLineCount(lines);
    }
  }, [props.name, props.partnerFullName]);

  const showLevel = props.level && props.type !== "Executive Education";
  const shouldShowCompletionTimeAndLevel = Boolean(
    completionTimeText || showLevel,
  );

  const skillsListText = props?.hoverOverlay?.skillsList?.join(", ");

  return (
    <div className="hidden md:block absolute z-10 bg-white w-full h-full opacity-0 transition-opacity duration-300 group-hover:opacity-100 overflow-hidden border border-[#CCCCCC] rounded-[12px]">
      {/* HEADER */}
      <div className={`w-full px-4 py-2 rounded-t-xl ${typeBackgroundColor}`}>
        <span className="font-bold text-primary-500 text-sm">
          {props.cta?.topCtaText || props.type}
        </span>
      </div>

      {/* BODY LINK - This will grow and can be truncated */}
      <a
        className="p-4 block h-full overflow-hidden"
        href={
          props.queryId ? getUrl(props.hrefWithQueryParams) : getUrl(props.href)
        }
        onClick={handleClick}
      >
        <div
          ref={titleRef}
          className="text-base font-bold mb-2 line-clamp-2 overflow-hidden text-ellipsis"
        >
          {`${props.partnerFullName}: ${props.name}`}
        </div>

        <div className=" mt-2 mb-4 font-bold text-xs flex flex-row flex-wrap gap-2">
          {props.showBadges && badges.length > 0 && (
            <div className="flex flex-row gap-2 flex-wrap ">{badges}</div>
          )}

          {shouldShowCompletionTimeAndLevel && (
            <div className="flex flex-row flex-wrap items-center">
              {completionTimeText && <span>{completionTimeText}</span>}
              {completionTimeText && showLevel && (
                <span className="mx-1">•</span>
              )}
              {showLevel && <span>{props.level}</span>}
            </div>
          )}
        </div>
        <div
          className={`text-xs ${getLineHeight(titleLineCount, badges.length > 0)} 
          [&_*]:text-xs [&_p]:!my-0 [&_ul]:!px-4 [&_li]:!py-0 flex flex-col gap-2 overflow-hidden`}
        >
          <div
            className="overflow-hidden text-ellipsis"
            style={{
              display: "-webkit-box",
              WebkitLineClamp: getWebkitLineClamp(
                titleLineCount,
                shouldShowCompletionTimeAndLevel,
              ),
              WebkitBoxOrient: "vertical",
            }}
          >
            {props.hoverOverlay?.primaryDescription &&
              props.hoverDataControl?.showPrimaryDescription &&
              sanitizeAndParseInlineHTML(props.hoverOverlay.primaryDescription)}
            {props.hoverOverlay?.aboutThisProduct &&
              props.hoverDataControl?.showAboutThisProduct &&
              sanitizeAndParseInlineHTML(props.hoverOverlay.aboutThisProduct)}
            {props.hoverOverlay?.learningBulletItems &&
              props.hoverDataControl?.showLearningBulletItems &&
              sanitizeAndParseInlineHTML(
                props.hoverOverlay.learningBulletItems,
              )}
            {skillsListText !== undefined &&
              props.hoverDataControl?.showSkillsList &&
              skillsListText.length > 0 && (
                <p>
                  <b>Skills covered in the course</b>: {skillsListText}
                </p>
              )}
          </div>
        </div>
      </a>

      {/* FOOTER LINK - This stays at the bottom */}
      {!hideCta && (
        <div className="absolute w-full bottom-0 p-4 bg-white">
          <Button
            variant="primaryOutline"
            asChild
            className="w-full border-2 font-normal text-primary-500 hover:bg-primary hover:text-white"
          >
            <a
              href={
                props.queryId
                  ? getUrl(props.hrefWithQueryParams)
                  : getUrl(props.href)
              }
              onClick={handleClickCta}
            >
              {ctaText}
            </a>
          </Button>
        </div>
      )}
    </div>
  );
}
